import { Operators } from '../constants/search';
import { Folder, FolderSelectionState } from '../types/tree';
import { generateFilePiiQLQuery, generateQueryFolders, PiiQLSearch } from './searchPiiQLGenerator';

/**
 * @param value - supposed megabyte value
 * @returns value in bytes or null if invalid
 */
function moToByte(value: unknown): string | null {
  if (value === null || (typeof value !== 'string' && typeof value !== 'number')) {
    return null;
  }
  if (typeof value === 'string') {
    value = parseFloat(value);
  }
  if (Number.isNaN(value)) {
    return null;
  }
  return ((value as number) * 1024 * 1024).toFixed(1).toString();
}

/**
 * Generates a filter query to search in the 'files' index
 *
 * @param searchGroups - groups of search items from the dam search. Each group will have
 * its own boolean operator between its items
 * @param treeRoots - the root folders (only those that have been loaded in the ui)
 * @param treeSelection - the current tree selection state
 * @returns globalQuery - Filter to execute filtered search
 */
export function generateFilesPiiQLQuery(
  searchGroups: PiiQLSearch.SearchItem[][],
  treeRoots: Folder[],
  treeSelection: FolderSelectionState
): string {
  // Clone search groups so we can mutate them without changing
  // the data in the component
  const queryAttributes = generateFilePiiQLQuery(JSON.parse(JSON.stringify(searchGroups)), {
    namePrefix: 'Name',
    join: Operators.And,
    transform: searchItem => {
      if (
        searchItem._attributeFilter?._queryFieldPath === 'Size' &&
        searchItem._attributeFilter.options.range &&
        Array.isArray(searchItem._valueObj)
      ) {
        // Change the size values since the user input is in megabytes
        // but we must query in bytes
        searchItem._valueObj = [
          moToByte(searchItem._valueObj[0]),
          moToByte(searchItem._valueObj[1]),
        ];
      }
      return searchItem;
    },
  });
  const queryFolders = generateQueryFolders(treeRoots, treeSelection);

  return [queryAttributes, queryFolders]
    .filter(Boolean)
    .map(q => `(${q})`)
    .join(` ${Operators.And} `);
}
